import {Debugger} from "debug";
import {MedlemsRolleModell, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erLikIgnorerCasing, erTomEllerNil, trimTilNull, trimTilTomStreng} from "felles-ng2/støttefunksjoner/strenger";
import {muligeTilgangsrollerForMedlem} from "felles-ng2/modell/medlemModellHjelpere";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {Temporal} from "temporal-polyfill";
import {lesPlainDateFraStreng} from "felles-ng2/støttefunksjoner/temporal";
import {Lokaleinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeLokale";

const debug: Debugger = require('debug')('HåndterInnlimteInvitasjoner');

/** Klasse for lesing av innlimte invitasjoner.
 *
 * Eksempel på gyldige data:
 <pre>
 1kai@systemfabrikken.no;Per pers;+4790967244;gruppemedlem;[Lesetilgang], [Administrator]
 2per1@systemfabrikken.no;Kai Lilleb;93022179;gruppeleder;[Lesetilgang]
 </pre>
 */
export class HåndterInnlimteInvitasjoner {

    innlimteData!: string;
    status = ParseStatus.ok;
    feiletLinje: number | null = null;

    private tilgjengeligeTilgangsroller = muligeTilgangsrollerForMedlem();
    private lesteLinjer: LestLinje[] = []

    constructor(hendelse: ClipboardEvent,
                private tilgjengeligeMedlemsroller: MedlemsRolleModell[],
                private enumOversettelser: EnumOversettelser,
                private lokale: Lokaleinformasjon) {
        debug('constructor', hendelse, tilgjengeligeMedlemsroller);
        const data = hendelse.clipboardData;

        if (erNil(data) || !data.types.includes('text/plain'))
            return this.markerSomUgyldig(ParseStatus.ikkeTekstInnliming, `forventet text/plain som tilgjengelig type, inneholdt ${data?.types}`);

        this.innlimteData = trimTilTomStreng(data.getData('text/plain'));

        if (!this.erInvitasjonerInnliming(this.innlimteData))
            return this.markerSomUgyldig(ParseStatus.ikkeInvitasjonerInnliming, `antar at innlimte data ikke var en invitasjoner-innliming: '${this.innlimteData}'`);

        const linjer = this.innlimteData.split(/[\n]/);
        if (linjer.length == 0)
            return this.markerSomUgyldig(ParseStatus.tom, `innlimte data var tom streng: '${this.innlimteData}'`);

        this.lesteLinjer = linjer
          .map(linje => linje.split(/[\t;]/))
          .filter((linje, i) => this.validerKolonner(linje, i + 1))
          .map((linje, i) => this.parseLestLinje(linje, i + 1))
    }

    hentLesteLinjer(): LestLinje[] {
        return this.lesteLinjer;
    }

    private erInvitasjonerInnliming(data: string) {
        if (!/.*\S+@\S+.*/.test(data)) return false; //forventer at data inneholder epostadresse
        if (!/[\t;]/.test(data)) return false; //forventer at data inneholder kolonneseparator (tab eller ;)
        return true;
    }

    private validerKolonner(kolonner: string[], linjenr: number): boolean {
        if (kolonner.length == 0) return false;
        if (kolonner.every(kolonne => erTomEllerNil(kolonne))) return false;
        if (kolonner.length < 6) {
            this.markerSomUgyldig(ParseStatus.feilantallkolonner, `linje ${linjenr} inneholder feil antall kolonner, forventet 6, var '${kolonner.length}', kolonner: ${kolonner}`, linjenr);
            return false;
        }
        return true;
    }

    private parseLestLinje(kolonner: string[], linjenr: number): LestLinje {
        const forespurtMedlemsrolle = trimTilNull(kolonner[3]);
        const medlemsrolle = this.tilgjengeligeMedlemsroller.find(rolle => erLikIgnorerCasing(rolle.tittel, forespurtMedlemsrolle)) || null;

        const medlemsrolleStartdato = this.lesMedlemsrolleStartdato(trimTilNull(kolonner[4]));
        const tilgangsrolle = trimTilTomStreng(kolonner[5]);
        const tilgangsrollerStr = tilgangsrolle.split(',').map(rolle => trimTilNull(rolle)).filter(rolle => erIkkeNil(rolle));
        const tilgangsroller: TilgangsrolleMedlem[] = this.tilgjengeligeTilgangsroller
          .filter(rolle => tilgangsrollerStr.some(tilgangsrolle => erLikIgnorerCasing(rolle.tittel(this.enumOversettelser), tilgangsrolle)))
          .map(rolle => rolle.rolle);
        const ugyldigeTilgangsroller = tilgangsrollerStr
          .filter(rolleStr => !this.tilgjengeligeTilgangsroller.some(rolle => erLikIgnorerCasing(rolle.tittel(this.enumOversettelser), rolleStr)))
          .join(', ');

        return {
            linjenr: linjenr,
            epost: trimTilNull(kolonner[0]),
            navn: trimTilNull(kolonner[1]),
            mobilnummer: trimTilNull(kolonner[2]),
            forespurtMedlemsrolle: forespurtMedlemsrolle,
            medlemsrolleEntitet: medlemsrolle,
            medlemsrolleStartdato: medlemsrolleStartdato,
            forespurteTilgangsroller: tilgangsrolle,
            ugyldigeTilgangsroller: ugyldigeTilgangsroller,
            tilgangsroller: tilgangsroller
        }
    }

    private lesMedlemsrolleStartdato(datostreng: string | null) {
        if (erNil(datostreng)) return null;
        return lesPlainDateFraStreng(trimTilTomStreng(datostreng), this.lokale);
    }

    private markerSomUgyldig(status: ParseStatus, årsak: string, linjenr: number | null = null): HåndterInnlimteInvitasjoner {
        debug(årsak);
        this.status = status;
        this.feiletLinje = linjenr
        return this;
    }
}

export interface LestLinje {
    linjenr: number;
    navn: string | null;
    epost: string | null;
    mobilnummer: string | null;
    forespurtMedlemsrolle: string | null;
    medlemsrolleEntitet: MedlemsRolleModell | null;
    medlemsrolleStartdato: Temporal.PlainDate | null;
    forespurteTilgangsroller: string;
    ugyldigeTilgangsroller: string;
    tilgangsroller: TilgangsrolleMedlem[];
}

export enum ParseStatus {
    ok = 'ok',
    tom = 'tom',
    ikkeInvitasjonerInnliming = 'ikkeInvitasjonerInnliming',
    ikkeTekstInnliming = 'ikkeTekstInnliming',
    feilantallkolonner = 'feilantallkolonner'
}
